import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Paper,
} from "@mui/material";
import {
  Map,
  AddLocationAlt,
  VerifiedUser,
  LocalFireDepartment,
  Timeline,
  FilterAlt,
  WarningAmber,
  Settings,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
const menuItems = [
  {
    title: "Interactive Map",
    icon: <Map fontSize="large" color="primary" />,
    description:
      "Explore approved land pricing records through interactive maps, markers, and popups. Users can visualize land values across regions and understand market trends geographically.",
  },

  {
    title: "Submit Land Data",
    icon: <AddLocationAlt fontSize="large" color="success" />,
    description:
      "Click directly on the map or use the submission panel to contribute new land pricing information including area, category, price, ownership details, and supporting metadata.",
  },

  {
    title: "Moderation",
    icon: <VerifiedUser fontSize="large" color="secondary" />,
    description:
      "Moderators validate submissions, approve legitimate records, reject invalid entries, and maintain data quality before information becomes publicly visible.",
  },

  {
    title: "Heatmaps",
    icon: <LocalFireDepartment fontSize="large" color="error" />,
    description:
      "Visualize high-value and low-value land clusters through dynamic heatmaps that help identify investment opportunities and market patterns.",
  },

  {
    title: "History & Analytics",
    icon: <Timeline fontSize="large" color="info" />,
    description:
      "Track historical land prices, market fluctuations, district-wise growth patterns, and analytical insights over time.",
  },

  {
    title: "Advanced Filters",
    icon: <FilterAlt fontSize="large" color="warning" />,
    description:
      "Filter land records based on residential, agricultural, industrial, commercial categories, price ranges, approval status, and locations.",
  },

  {
    title: "Anomaly Detection",
    icon: <WarningAmber fontSize="large" color="error" />,
    description:
      "Automatically detect suspicious pricing entries, outliers, duplicate submissions, and abnormal market values using intelligent validation mechanisms.",
  },

  {
    title: "Settings",
    icon: <Settings fontSize="large" color="action" />,
    description:
      "Manage user preferences, currency conversion settings, map themes, notification preferences, and platform configurations.",
  },
];

export default function Dashhome() {


   const navigate = useNavigate();
  return (
    <Box
      sx={{
        p: 4,
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}

      <Paper
        elevation={3}
        sx={{
          p: 5,
          borderRadius: 4,
          background:
            "linear-gradient(135deg,#0f172a,#1e293b,#0f766e)",
          color: "white",
          mb: 5,
        }}
      >
        <Typography variant="h3" fontWeight="bold">
          Welcome to Land Pricing Intelligence Platform
        </Typography>

        <Typography
          variant="h6"
          sx={{
            mt: 2,
            opacity: 0.9,
            maxWidth: "900px",
            lineHeight: 1.8,
          }}
        >
          A modern geospatial platform designed to collect,
          validate, moderate, analyze, and visualize land pricing
          information across regions.
        </Typography>
<Button
    variant="contained"
    size="large"
    // startIcon={<MapIcon />}
    onClick={() => navigate("/mappage")}
    sx={{
      mt: 4,
      px: 4,
      py: 1.5,
      borderRadius: 3,
      textTransform: "none",
      fontSize: "16px",
      fontWeight: 600,
      backgroundColor: "#2563EB",

      "&:hover": {
        backgroundColor: "#1D4ED8",
      },
    }}
  >
    Get Started
  </Button>
      </Paper>

      {/* Platform Workflow */}

      <Paper
        elevation={2}
        sx={{
          p: 4,
          borderRadius: 4,
          mb: 5,
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Platform Workflow
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Typography
          sx={{
            lineHeight: 2,
            fontSize: "16px",
          }}
        >
          User Opens Platform
          → Views Interactive Map
          → Clicks On Map
          → Submits Land Information
          → Backend Validation
          → Anomaly Detection
          → Pending Review
          → Moderator Approval
          → Published On Map
          → Heatmap Generation
          → Historical Analytics
        </Typography>
      </Paper>

      {/* Dashboard Guide */}

      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ mb: 4 }}
      >
        Dashboard Guide
      </Typography>

      {/* <Grid container spacing={3}>
        {menuItems.map((item, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                transition: "0.3s",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                <Box mb={2}>{item.icon}</Box>

                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                >
                  {item.title}
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid> */}


      
<Grid container spacing={3}>
  {menuItems.map((item) => (
    <Grid item xs={12} md={6} key={item.title}>
      <Card
        sx={{
          height: 220,        // SAME HEIGHT
          borderRadius: 4,
          p: 4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",

          transition: "0.3s",

          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: 6,
          },
        }}
      >
        {item.icon}

        <Typography
          sx={{
            mt: 2,
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          {item.title}
        </Typography>

        <Typography
          sx={{
            mt: 1,
            color: "#475569",
            lineHeight: 1.7,
          }}
        >
          {item.description}
        </Typography>

      </Card>
    </Grid>
  ))}
</Grid>

      {/* Key Benefits */}

      <Paper
        elevation={2}
        sx={{
          mt: 6,
          p: 4,
          borderRadius: 4,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Why Use This Platform?
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Typography variant="h6">
              📍 Accurate Land Insights
            </Typography>

            <Typography color="text.secondary">
              Verified and moderated land data ensures
              trustworthy pricing information.
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="h6">
              🔥 Heatmap Intelligence
            </Typography>

            <Typography color="text.secondary">
              Identify emerging hotspots and premium
              investment zones visually.
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="h6">
              📈 Historical Analytics
            </Typography>

            <Typography color="text.secondary">
              Monitor price growth, market shifts,
              and long-term trends effectively.
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="h6">
              🛡️ Data Validation
            </Typography>

            <Typography color="text.secondary">
              Automated anomaly detection and moderation
              workflows maintain data quality.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Footer */}

      <Box
        sx={{
          mt: 6,
          textAlign: "center",
          color: "gray",
        }}
      >
        <Typography>
          Smartplot Land Pricing Intelligence Platform © 2026
        </Typography>

        <Typography variant="body2">
          Enabling transparent, data-driven land market decisions.
        </Typography>
      </Box>
    </Box>
  );
}